import { useContext, useEffect, useRef, useState } from 'react';
import DeleteIcon from "src/assets/all-icons/delete-icon.jsx";
import EditArtworkIcon from "src/assets/all-icons/edit-artwork-icon.jsx";
import PlayIcon from "src/assets/all-icons/play-icon.jsx";

import AddToArtlistIcon from '@/assets/all-icons/add-to-artlist-icon';
import ArtlistDataContext from '@/context/ArtlistDataContext';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import LikeButton from '@/shared-components/buttons/components/LikeButton';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import ArtlistOption from './components/ArtlistOption';
import DeleteArtlist from './components/DeleteArtlist';
import EditArtlistForm from './edit-artlist/edit-artlist-form';
import AddArtworks from './add-artworks';
import TimeSelector from './time-selector';

import './index.css';

export default function Options({ openAddArtworks, setOpenAddArtworks, autoOpenTimeSelector = false }) {
  const { artlistData, setArtlistData, isCreator } = useContext(ArtlistDataContext);
  const t = useTranslations('common.profile.pages.artlist');
  const [openOptions, setOpenOptions] = useState(false);
  const [openTimeSelector, setOpenTimeSelector] = useState(false);
  const [openDeleteArtlist, setOpenDeleteArtlist] = useState(false);
  const [openEditArtlist, setOpenEditArtlist] = useState(false);
  const menuRef = useRef(null);
  const menuTriggerRef = useRef(null);
  const privateActionHandler = useProtectAction({ typeOfProtection: 'USER_ACCOUNT_REQUIRED' });
  const { state, stateHandler } = useStateHandler();
  const autoOpenHandledRef = useRef(false);
  const session = state.user_session;
  const creatorId = artlistData?.artlist_creator?._id;
  const totalArtworks = artlistData?.artlist_artworks_total ?? artlistData?.artlist_artworks?.length ?? 0;
  const canEditArtlist = isCreator
    || canAccessGalleryPermission(session, "artlist.edit", creatorId);
  const canDeleteArtlist = isCreator
    || canAccessGalleryPermission(session, "artlist.delete", creatorId);
  const canManageArtlist = canEditArtlist || canDeleteArtlist;
  const likesCount = Number(artlistData.artlist_likes) || 0;

  function handleOpenTimeSelector() {
    stateHandler.set(
      'unregisteredUser',
      ['guest_artlist_play', artlistData.artlist_creator?.user_username]
    );
    privateActionHandler(() => setOpenTimeSelector(true));
  }

  function handleMenuKeyDown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    setOpenOptions(false);
    menuTriggerRef.current?.focus();
  }

  useEffect(() => {
    if (
      !autoOpenTimeSelector ||
      autoOpenHandledRef.current ||
      totalArtworks === 0
    ) {
      return;
    }

    autoOpenHandledRef.current = true;
    stateHandler.set(
      'unregisteredUser',
      ['guest_artlist_play', artlistData.artlist_creator?.user_username]
    );
    privateActionHandler(() => setOpenTimeSelector(true));
  }, [
    autoOpenTimeSelector,
    totalArtworks,
    artlistData?.artlist_creator?.user_username,
    privateActionHandler,
    stateHandler,
  ]);

  useEffect(() => {
    if (!openOptions) {
      return undefined;
    }

    function closeMenuOnOutsidePointer(event) {
      if (
        !menuRef.current?.contains(event.target)
        && !menuTriggerRef.current?.contains(event.target)
      ) {
        setOpenOptions(false);
      }
    }

    document.addEventListener('pointerdown', closeMenuOnOutsidePointer);
    return () => document.removeEventListener('pointerdown', closeMenuOnOutsidePointer);
  }, [openOptions]);

  return (
    <>
      <div className="artlist-options__container">
        {totalArtworks > 0 && (
          <button
            type="button"
            className="artlist-options__artlist-play"
            onClick={handleOpenTimeSelector}
            aria-label={t('time_selector.actions.play_artlist')}
          >
            <PlayIcon aria-hidden="true" />
          </button>
        )}

        <LikeButton
          itemId={artlistData._id}
          typeOfItem="ARTLIST"
          isLiked={artlistData.is_liked}
          className="artlist-options__artlist-like"
          unregisteredIntent={['guest_artlist_like', artlistData.artlist_creator?.user_username]}
          onLikeAction={(likeStatus) =>
            setArtlistData({
              ...artlistData,
              artlist_likes: likeStatus
                ? likesCount + 1
                : Math.max(0, likesCount - 1),
            })
          }
        />

        {canManageArtlist ? (
          <div className="artlist-options__menu">
            <button
              ref={menuTriggerRef}
              type="button"
              className="artlist-options__threedots-container"
              onClick={() => setOpenOptions((v) => !v)}
              aria-expanded={openOptions}
              aria-haspopup="menu"
              aria-label={t('options.menu.more_actions')}
              onKeyDown={handleMenuKeyDown}
            >
              <ThreeDotsHorizontal className="artlist-options__threedots-icon" aria-hidden="true" />
            </button>

            <div
              ref={menuRef}
              className={`artlist-options__options ${openOptions ? 'active' : ''}`}
              role="menu"
              onKeyDown={handleMenuKeyDown}
            >
              {canEditArtlist && (
                <>
                  <ArtlistOption
                    text={t('options.menu.add_artworks')}
                    icon={<AddToArtlistIcon className="artlist-options__option-icon" />}
                    onClick={() => {
                      setOpenOptions(false);
                      setOpenAddArtworks(true);
                    }}
                  />

                  <ArtlistOption
                    text={t('options.menu.edit_artlist')}
                    icon={<EditArtworkIcon className="artlist-options__option-icon edit" />}
                    onClick={() => {
                      setOpenOptions(false);
                      setOpenEditArtlist(true);
                    }}
                  />
                </>
              )}

              {canDeleteArtlist && (
                <ArtlistOption
                  text={t('options.menu.delete_artlist')}
                  icon={<DeleteIcon className="artlist-options__option-icon" />}
                  onClick={() => {
                    setOpenOptions(false);
                    setOpenDeleteArtlist(true);
                  }}
                />
              )}

            </div>
          </div>
        ) : null}
      </div>

      <TimeSelector
        open={openTimeSelector}
        close={() => setOpenTimeSelector(false)}
      />

      <AddArtworks
        open={openAddArtworks}
        close={() => setOpenAddArtworks(false)}
      />

      {openEditArtlist && (
        <EditArtlistForm
          open={openEditArtlist}
          close={() => setOpenEditArtlist(false)}
        />
      )}

      <DeleteArtlist
        open={openDeleteArtlist}
        close={() => setOpenDeleteArtlist(false)}
      />
    </>
  );
}
